/**
 * V1.8.6 — Door/window opening helpers for FloorPlan2D.
 *
 * V1.8.7 extends the data model to support multiple door + window TYPES
 * (per real architectural conventions):
 *   Doors:    swing | sliding | pocket | bifold
 *   Windows:  fixed | sliding | casement | double_hung
 *
 * Two layers of data:
 *   1. AI-emitted explicit arrays: `doors`/`windows` on the floor object.
 *      Each entry: `{ id?, type?, room_a?, room_b?, room?, side?, position? }`.
 *      Used as the source of truth when present.
 *   2. Heuristic auto-inference: when explicit arrays are missing or empty,
 *      fall back to `findInteriorDoorPairs` + exterior window placement.
 *
 * Outputs of the public helpers are pure data ({axis, coord, span_lo, ...})
 * — the caller (FloorPlan2D) handles the SVG drawing in canvas pixel space.
 */

// V2.0.74 — Tolerance bumped from 0.05m → 0.20m. When Gemini regenerates
// a floor plan it often introduces sub-decimetre coordinate drift between
// adjacent rooms (e.g. two rooms that should share a wall end up 8-15cm
// apart). At the old 5cm threshold both edges of the "shared" wall were
// classified as EXTERIOR and rendered as blue exterior walls smack in the
// middle of the interior. 20cm is architecturally still tighter than any
// real gap a homeowner would leave between rooms (~8 inches), so we lose
// nothing legitimate.
const TOL = 0.20; // meters — how close two walls must be to count as shared
const MIN_SHARED_EDGE = 1.0; // door fits if shared edge ≥ 1m
const DOOR_RADIUS_M = 0.76; // standard 30" door arc

// V2.0.9 — Merged-room helper (mirrors lib/walls.js). When two rooms are
// merged via `merged_with: [...]`, we treat their shared interior wall as
// non-existent: no auto-door pair, no exterior wall sub-segments, no
// hover-add target on the merged edge.
function isMergedPair(roomA, roomB) {
  if (!roomA || !roomB || roomA.id === roomB.id) return false;
  const mA = Array.isArray(roomA.merged_with) ? roomA.merged_with : [];
  const mB = Array.isArray(roomB.merged_with) ? roomB.merged_with : [];
  return mA.includes(roomB.id) || mB.includes(roomA.id);
}

/**
 * Find every adjacent-room pair sharing a wall ≥ 1m long. One door per pair.
 *
 * Returns array of:
 *   { a, b, axis: 'x'|'z', coord, span_lo, span_hi, bigger, type, id }
 * - axis='z' = shared wall runs along z, so it's a constant-x vertical wall.
 * - axis='x' = shared wall runs along x, so it's a constant-z horizontal wall.
 * - bigger  = the larger of the two rooms — used to decide swing direction.
 * - type    = "swing" by default, overridden by AI-emitted doors[].type.
 * - id      = "D1", "D2", ... assigned in iteration order for the door tag.
 */
export function findInteriorDoorPairs(rooms, explicitDoors = []) {
  const pairs = [];
  const valid = rooms.filter(
    (r) =>
      r &&
      Number.isFinite(r.x) &&
      Number.isFinite(r.z) &&
      r.width > 0 &&
      r.depth > 0,
  );
  // Build an explicit-override lookup keyed by sorted (room_a, room_b) tuple.
  // V1.9.2 — capture the explicit array index too so the click-to-edit
  // popover can mutate the right row.
  // V1.9.9 — `deleted: true` tombstones suppress BOTH explicit rendering
  // AND the matching auto-inference, so users can permanently remove
  // AI-placed doors.
  const overrideByPair = new Map();
  const suppressedPairs = new Set();
  (explicitDoors || []).forEach((d, idx) => {
    if (!d || !d.room_a || !d.room_b) return;
    const k = [d.room_a, d.room_b].sort().join("|");
    if (d.deleted) {
      suppressedPairs.add(k);
      return;
    }
    overrideByPair.set(k, {
      type: String(d.type || "swing").toLowerCase(),
      id: d.id || null,
      position: Number.isFinite(d.position) ? d.position : null,
      // V2.0.1 — manual swing-direction + hinge-end overrides.
      hinge: d.hinge || null,
      swing_direction: d.swing_direction || null,
      _explicitIdx: idx,
      _explicit: { ...d },
    });
  });

  for (let i = 0; i < valid.length; i++) {
    const a = valid[i];
    for (let j = i + 1; j < valid.length; j++) {
      const b = valid[j];
      const ax0 = a.x, ax1 = a.x + a.width;
      const bx0 = b.x, bx1 = b.x + b.width;
      const az0 = a.z, az1 = a.z + a.depth;
      const bz0 = b.z, bz1 = b.z + b.depth;
      const oKey = [a.id, b.id].sort().join("|");
      if (suppressedPairs.has(oKey)) continue;
      // V2.0.9 — Skip auto door pair if the two rooms are merged (open
      // plan / L-shape). Their shared wall doesn't exist, so neither
      // does the door.
      if (isMergedPair(a, b)) continue;
      const o = overrideByPair.get(oKey);
      // Constant-x shared walls.
      for (const [axA, axB] of [[ax1, bx0], [ax0, bx1]]) {
        if (Math.abs(axA - axB) < TOL) {
          const zLo = Math.max(az0, bz0);
          const zHi = Math.min(az1, bz1);
          if (zHi - zLo > MIN_SHARED_EDGE) {
            const bigger = a.width * a.depth >= b.width * b.depth ? a : b;
            pairs.push({
              a, b, axis: "z", coord: axA,
              span_lo: zLo, span_hi: zHi, bigger,
              len: DOOR_RADIUS_M,  // V1.9.4 — door slab width for drag clamping
              type: o?.type || "swing",
              id: o?.id || null,
              position: Number.isFinite(o?.position) ? o.position : null,
              hinge: o?.hinge || null,
              swing_direction: o?.swing_direction || null,
              _explicitIdx: o?._explicitIdx ?? -1,
              _explicitRoomA: o?._explicit?.room_a || null,
              _explicitRoomB: o?._explicit?.room_b || null,
              _autoRoomA: a.id, _autoRoomB: b.id,
            });
          }
        }
      }
      // Constant-z shared walls.
      for (const [azA, azB] of [[az1, bz0], [az0, bz1]]) {
        if (Math.abs(azA - azB) < TOL) {
          const xLo = Math.max(ax0, bx0);
          const xHi = Math.min(ax1, bx1);
          if (xHi - xLo > MIN_SHARED_EDGE) {
            const bigger = a.width * a.depth >= b.width * b.depth ? a : b;
            pairs.push({
              a, b, axis: "x", coord: azA,
              span_lo: xLo, span_hi: xHi, bigger,
              len: DOOR_RADIUS_M,  // V1.9.4 — door slab width for drag clamping
              type: o?.type || "swing",
              id: o?.id || null,
              position: Number.isFinite(o?.position) ? o.position : null,
              hinge: o?.hinge || null,
              swing_direction: o?.swing_direction || null,
              _explicitIdx: o?._explicitIdx ?? -1,
              _explicitRoomA: o?._explicit?.room_a || null,
              _explicitRoomB: o?._explicit?.room_b || null,
              _autoRoomA: a.id, _autoRoomB: b.id,
            });
          }
        }
      }
    }
  }
  // V2.0.7 — Exterior doors. Stored window-style with `is_exterior: true`,
  // `room_a` (the interior room), `side`, and `position`. We synthesise a
  // pair that the door-arc renderer can consume just like an interior
  // pair — the "bigger" side is the EXTERIOR direction (away from the
  // room) so the arc swings outdoors by default.
  const byRoomId = new Map(valid.map((r) => [r.id, r]));
  (explicitDoors || []).forEach((d, idx) => {
    if (!d || !d.is_exterior) return;
    if (d.deleted) return;
    const room = byRoomId.get(d.room_a);
    if (!room) return;
    const side = String(d.side || "front").toLowerCase();
    const pos = Number.isFinite(d.position) ? d.position : 0.5;
    const w = Number.isFinite(d.width) ? d.width : 0.9;
    // Synthesise span + coord based on side. The "outside" axis position
    // is half the door width past the room boundary so the bigger-room
    // heuristic picks the EXTERIOR direction.
    let axis, coord, span_lo, span_hi;
    // The synthetic exterior anchor is offset just OUTSIDE the wall so
    // any geometry that picks "bigger" by comparing a.x vs the wall coord
    // resolves to the outdoor side.
    let bigger;
    if (side === "front") {
      axis = "x"; coord = room.z;
      span_lo = room.x + room.width * pos - w / 2;
      span_hi = room.x + room.width * pos + w / 2;
      bigger = { x: room.x, z: room.z - 1, width: room.width, depth: 0.1 };
    } else if (side === "rear") {
      axis = "x"; coord = room.z + room.depth;
      span_lo = room.x + room.width * pos - w / 2;
      span_hi = room.x + room.width * pos + w / 2;
      bigger = { x: room.x, z: room.z + room.depth + 1, width: room.width, depth: 0.1 };
    } else if (side === "left") {
      axis = "z"; coord = room.x;
      span_lo = room.z + room.depth * pos - w / 2;
      span_hi = room.z + room.depth * pos + w / 2;
      bigger = { x: room.x - 1, z: room.z, width: 0.1, depth: room.depth };
    } else {
      axis = "z"; coord = room.x + room.width;
      span_lo = room.z + room.depth * pos - w / 2;
      span_hi = room.z + room.depth * pos + w / 2;
      bigger = { x: room.x + room.width + 1, z: room.z, width: 0.1, depth: room.depth };
    }
    pairs.push({
      a: room, b: room, axis, coord,
      span_lo, span_hi, bigger,
      len: DOOR_RADIUS_M,
      type: String(d.type || "single").toLowerCase(),
      id: d.id || `D-ext-${idx}`,
      position: 0.5, // already encoded in span_lo/span_hi
      hinge: d.hinge || null,
      swing_direction: d.swing_direction || null,
      _explicitIdx: idx,
      _explicitRoomA: d.room_a, _explicitRoomB: null,
      _autoRoomA: d.room_a, _autoRoomB: null,
      _isExterior: true,
    });
  });
  // V2.0.0 — Stable identity-based IDs (auto-D-<roomA>-<roomB>) so the
  // React key stays the same when an auto-inferred door is materialised
  // into an explicit row mid-interaction. Without this, the overlay rect
  // unmounts/remounts between pointerdown and click, and the click never
  // reaches handleOpeningClick. Explicit rows preserve their incoming id.
  pairs.forEach((p) => {
    if (!p.id) {
      const ids = [p._autoRoomA, p._autoRoomB].filter(Boolean).sort();
      p.id = ids.length === 2 ? `auto-D-${ids[0]}-${ids[1]}` : `auto-D-${p.axis}-${p.coord.toFixed(2)}`;
    }
  });
  // V2.0.0 — defensive dedupe: if the explicit doors[] array contains
  // duplicate ids (seed data quirk), append the pair's sorted room ids
  // to keep React keys + data-testids unique. The first occurrence
  // keeps its bare id.
  const seenIds = new Map();
  pairs.forEach((p) => {
    const n = (seenIds.get(p.id) || 0) + 1;
    seenIds.set(p.id, n);
    if (n > 1) {
      const ids = [p._autoRoomA, p._autoRoomB].filter(Boolean).sort();
      p.id = `${p.id}-${ids.join("-") || `${p.axis}-${p.coord.toFixed(2)}`}-${n}`;
    }
  });
  return pairs;
}

/**
 * Find exterior walls — the segments of the building footprint that don't
 * sit on a shared interior boundary. Returns segments as
 *   { axis: 'x'|'z', coord, span_lo, span_hi, length }
 * where axis names the direction the segment RUNS along (so 'x' axis = a
 * horizontal exterior wall).
 *
 * Used to auto-place window glyphs on long exterior runs.
 */
export function findExteriorWallSegments(rooms) {
  const valid = rooms.filter(
    (r) => r && r.width > 0 && r.depth > 0,
  );
  // Map our internal candidate.side ("bottom"/"top"/"left"/"right") to the
  // "front"/"rear"/"left"/"right" convention used by computeWindows. Front
  // is the LOWER z edge (closer to the viewer) — see DOORS / WINDOWS prompt.
  const sideName = (s) => (s === "bottom" ? "front" : s === "top" ? "rear" : s);
  // For each room, each of its 4 wall edges contributes a candidate segment.
  // A candidate becomes "exterior" if no OTHER room's edge shares its line.
  const candidates = [];
  valid.forEach((r) => {
    candidates.push({ room: r, axis: "x", coord: r.z,          span_lo: r.x, span_hi: r.x + r.width, side: "bottom" });
    candidates.push({ room: r, axis: "x", coord: r.z + r.depth, span_lo: r.x, span_hi: r.x + r.width, side: "top" });
    candidates.push({ room: r, axis: "z", coord: r.x,           span_lo: r.z, span_hi: r.z + r.depth, side: "left" });
    candidates.push({ room: r, axis: "z", coord: r.x + r.width, span_lo: r.z, span_hi: r.z + r.depth, side: "right" });
  });
  // For each candidate, subtract any overlapping segments from neighbours
  // sitting on the SAME line (same axis + same coord). Whatever portion
  // remains is exterior. We return the remaining run intervals.
  const out = [];
  candidates.forEach((c) => {
    const overlaps = candidates.filter(
      (o) =>
        o !== c &&
        o.room !== c.room &&
        o.axis === c.axis &&
        Math.abs(o.coord - c.coord) < TOL,
    );
    // Mark portions of [c.span_lo, c.span_hi] covered by overlaps.
    let segments = [[c.span_lo, c.span_hi]];
    overlaps.forEach((o) => {
      const next = [];
      segments.forEach(([s, e]) => {
        // Subtract [o.span_lo, o.span_hi].
        const oLo = Math.max(s, o.span_lo);
        const oHi = Math.min(e, o.span_hi);
        if (oHi - oLo <= TOL) {
          next.push([s, e]);
          return;
        }
        if (oLo - s > TOL) next.push([s, oLo]);
        if (e - oHi > TOL) next.push([oHi, e]);
      });
      segments = next;
    });
    segments.forEach(([s, e]) => {
      if (e - s > 0.6) {
        out.push({
          axis: c.axis, coord: c.coord, span_lo: s, span_hi: e, length: e - s,
          // V1.9.5 — keep enough room/side context so an auto-inferred window
          // can be materialised into an explicit `windows[]` row on first drag.
          room_id: c.room.id,
          side: sideName(c.side),
        });
      }
    });
  });
  // Dedupe (each shared exterior edge will be reported once per adjacent room).
  const key = (s) => `${s.axis}_${s.coord.toFixed(2)}_${s.span_lo.toFixed(2)}_${s.span_hi.toFixed(2)}`;
  const seen = new Set();
  return out.filter((s) => {
    const k = key(s);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * V2.0.7 — Return EVERY wall segment in the plan (exterior AND interior),
 * classified by `kind`. Used by the desktop wall-click handler to detect
 * which wall the user clicked on and offer the right add-opening UI.
 *
 * Returns: [{ kind: "exterior"|"interior", axis, coord, span_lo, span_hi,
 *             // for exterior:
 *             room_id, side,
 *             // for interior (shared between two rooms):
 *             room_a, room_b }]
 */
export function findAllWallSegments(rooms) {
  const valid = rooms.filter((r) => r && r.width > 0 && r.depth > 0);
  const sideName = (s) => (s === "bottom" ? "front" : s === "top" ? "rear" : s);
  // Every candidate edge: 4 per room (bottom/top/left/right).
  const candidates = [];
  valid.forEach((r) => {
    candidates.push({ room: r, axis: "x", coord: r.z,          span_lo: r.x, span_hi: r.x + r.width, side: "bottom" });
    candidates.push({ room: r, axis: "x", coord: r.z + r.depth, span_lo: r.x, span_hi: r.x + r.width, side: "top" });
    candidates.push({ room: r, axis: "z", coord: r.x,           span_lo: r.z, span_hi: r.z + r.depth, side: "left" });
    candidates.push({ room: r, axis: "z", coord: r.x + r.width, span_lo: r.z, span_hi: r.z + r.depth, side: "right" });
  });
  const out = [];
  const seenKey = new Set();
  candidates.forEach((c) => {
    const overlaps = candidates.filter(
      (o) =>
        o !== c &&
        o.room !== c.room &&
        o.axis === c.axis &&
        Math.abs(o.coord - c.coord) < TOL,
    );
    // Build EXTERIOR sub-segments by subtracting overlap regions.
    let exteriorSegs = [[c.span_lo, c.span_hi]];
    // Build INTERIOR (shared) sub-segments — the OVERLAPS themselves —
    // paired with the other room so the UI knows room_a + room_b.
    const interiorSegs = [];
    overlaps.forEach((o) => {
      const oLo = Math.max(c.span_lo, o.span_lo);
      const oHi = Math.min(c.span_hi, o.span_hi);
      if (oHi - oLo > TOL) {
        interiorSegs.push({ span_lo: oLo, span_hi: oHi, room_a: c.room.id, room_b: o.room.id });
      }
      // Subtract from exteriorSegs.
      const next = [];
      exteriorSegs.forEach(([s, e]) => {
        const eLo = Math.max(s, o.span_lo);
        const eHi = Math.min(e, o.span_hi);
        if (eHi - eLo <= TOL) { next.push([s, e]); return; }
        if (eLo - s > TOL) next.push([s, eLo]);
        if (e - eHi > TOL) next.push([eHi, e]);
      });
      exteriorSegs = next;
    });
    // Emit exterior sub-segments (one per remaining gap).
    exteriorSegs.forEach(([s, e]) => {
      if (e - s <= 0.4) return;
      const key = `ex|${c.axis}_${c.coord.toFixed(2)}_${s.toFixed(2)}_${e.toFixed(2)}`;
      if (seenKey.has(key)) return;
      seenKey.add(key);
      out.push({
        kind: "exterior", axis: c.axis, coord: c.coord,
        span_lo: s, span_hi: e, length: e - s,
        room_id: c.room.id, side: sideName(c.side),
      });
    });
    // Emit interior sub-segments. Dedup by sorted room pair + coord/span.
    // V2.0.9 — skip merged pairs so the user can't click-add a door on a
    // wall that doesn't exist.
    interiorSegs.forEach((seg) => {
      const roomA = candidates.find((cc) => cc.room.id === seg.room_a)?.room;
      const roomB = candidates.find((cc) => cc.room.id === seg.room_b)?.room;
      if (isMergedPair(roomA, roomB)) return;
      const pair = [seg.room_a, seg.room_b].sort();
      const key = `in|${pair[0]}|${pair[1]}|${c.axis}_${c.coord.toFixed(2)}_${seg.span_lo.toFixed(2)}_${seg.span_hi.toFixed(2)}`;
      if (seenKey.has(key)) return;
      seenKey.add(key);
      out.push({
        kind: "interior", axis: c.axis, coord: c.coord,
        span_lo: seg.span_lo, span_hi: seg.span_hi,
        length: seg.span_hi - seg.span_lo,
        room_a: seg.room_a, room_b: seg.room_b,
      });
    });
  });
  return out;
}

/**
 * V2.0.7 — Hit-test a click in world coordinates against every wall
 * segment in the plan. Returns the CLOSEST wall within `tolerance_m`
 * metres of the click, or null. The returned object includes the
 * fractional `position` (0..1) along the wall axis so the caller can
 * place the new opening at the click point.
 */
export function findWallAtPoint(rooms, x_m, z_m, tolerance_m = 0.6) {
  const segs = findAllWallSegments(rooms);
  let best = null;
  let bestDist = tolerance_m;
  segs.forEach((s) => {
    let dist, pos;
    if (s.axis === "x") {
      // Horizontal wall — coord is z, span runs along x.
      dist = Math.abs(z_m - s.coord);
      if (x_m < s.span_lo - tolerance_m || x_m > s.span_hi + tolerance_m) return;
      const clamped = Math.min(s.span_hi, Math.max(s.span_lo, x_m));
      pos = (clamped - s.span_lo) / (s.span_hi - s.span_lo);
    } else {
      // Vertical wall — coord is x, span runs along z.
      dist = Math.abs(x_m - s.coord);
      if (z_m < s.span_lo - tolerance_m || z_m > s.span_hi + tolerance_m) return;
      const clamped = Math.min(s.span_hi, Math.max(s.span_lo, z_m));
      pos = (clamped - s.span_lo) / (s.span_hi - s.span_lo);
    }
    if (dist < bestDist) {
      bestDist = dist;
      best = { ...s, position: pos, hit_dist: dist };
    }
  });
  return best;
}

/**
 * Pick (up to) N evenly-spaced window centres along an exterior wall segment.
 * Returns an array of metre offsets along the segment.
 */
export function pickWindowCenters(seg, maxRunPerWindow = 3.5) {
  const len = seg.span_hi - seg.span_lo;
  if (len < 1.5) return [];
  const n = Math.max(1, Math.min(Math.floor(len / maxRunPerWindow), 4));
  return Array.from({ length: n }, (_, k) => seg.span_lo + (len / (n + 1)) * (k + 1));
}

/**
 * Build the final window-symbol list. Honors AI-emitted `windows` array when
 * present (one symbol per entry, with explicit `type`), otherwise falls back
 * to evenly-spaced auto placement on exterior wall segments.
 *
 * Each returned symbol:
 *   { id, axis, coord, centre, len, type }
 */
export function computeWindows(rooms, explicitWindows = []) {
  const out = [];
  const explicit = (explicitWindows || []).filter(
    (w) => w && typeof w === "object",
  );
  // V2.0.0 — collect (room, side) keys + per-center tombstones for
  // deleted explicit windows. Per-slot tombstoning (the V1.9.9 behaviour)
  // killed EVERY auto-window on the same wall when the user deleted just
  // one of them — the visible bug "deletes a different window than the
  // one I clicked". The fix: tombstones are per-center (with 0.8m
  // tolerance), so only the specific auto-window center is suppressed.
  const tombstonedCentersBySlot = new Map(); // slot -> array of centers
  const explicitCentersBySlot = new Map(); // slot -> array of centers
  const byId = new Map(rooms.map((r) => [r.id, r]));
  explicit.forEach((w, i) => {
    const r = byId.get(w.room) || byId.get(w.room_id);
    if (!r) return;
    let side = String(w.side || "front").toLowerCase();
    if (!["front", "rear", "left", "right"].includes(side)) side = "front";
    const slot = `${r.id}|${side}`;
    const pos = Number.isFinite(w.position) ? w.position : 0.5;
    // Compute the absolute centre in world metres along the wall axis.
    const tombCentre =
      side === "front" || side === "rear"
        ? r.x + r.width * pos
        : r.z + r.depth * pos;
    if (w.deleted) {
      if (!tombstonedCentersBySlot.has(slot)) tombstonedCentersBySlot.set(slot, []);
      tombstonedCentersBySlot.get(slot).push(tombCentre);
      return; // do NOT render
    }
    const winLen = Number.isFinite(w.width) ? Number(w.width) : 1.2;
    const type = String(w.type || "fixed").toLowerCase();
    const id = w.id || `W${out.length + 1}`;
    const _explicitIdx = i;
    let entry;
    if (side === "front") {
      entry = { id, type, axis: "x", coord: r.z, centre: r.x + r.width * pos, len: winLen, _explicitIdx, span_lo: r.x, span_hi: r.x + r.width };
    } else if (side === "rear") {
      entry = { id, type, axis: "x", coord: r.z + r.depth, centre: r.x + r.width * pos, len: winLen, _explicitIdx, span_lo: r.x, span_hi: r.x + r.width };
    } else if (side === "left") {
      entry = { id, type, axis: "z", coord: r.x, centre: r.z + r.depth * pos, len: winLen, _explicitIdx, span_lo: r.z, span_hi: r.z + r.depth };
    } else {
      entry = { id, type, axis: "z", coord: r.x + r.width, centre: r.z + r.depth * pos, len: winLen, _explicitIdx, span_lo: r.z, span_hi: r.z + r.depth };
    }
    out.push(entry);
    if (!explicitCentersBySlot.has(slot)) explicitCentersBySlot.set(slot, []);
    explicitCentersBySlot.get(slot).push(entry.centre);
  });

  // V2.0.0 — auto-inference always runs; per-segment centers are filtered
  // against BOTH the explicit list (so existing windows aren't double-drawn)
  // AND the per-center tombstone list (so a single deleted auto-window
  // doesn't kill its siblings on the same wall).
  findExteriorWallSegments(rooms).forEach((seg) => {
    const slot = `${seg.room_id}|${seg.side}`;
    const tombstoneCenters = tombstonedCentersBySlot.get(slot) || [];
    const explicitCenters = explicitCentersBySlot.get(slot) || [];
    const centers = pickWindowCenters(seg, 3.5);
    const winLen = centers.length
      ? Math.min(1.5, (seg.span_hi - seg.span_lo) / (centers.length + 1))
      : 0;
    centers.forEach((mc) => {
      // Skip if there's an explicit window within 0.8 m on the same wall —
      // they'd visually overlap and confuse the user.
      if (explicitCenters.some((ec) => Math.abs(ec - mc) < 0.8)) return;
      // Skip ONLY this specific auto-window if its centre is tombstoned.
      if (tombstoneCenters.some((tc) => Math.abs(tc - mc) < 0.8)) return;
      const position = seg.span_hi !== seg.span_lo
        ? (mc - seg.span_lo) / (seg.span_hi - seg.span_lo)
        : 0.5;
      // V2.0.0 — stable identity-based id so the React key for this
      // overlay rect doesn't change when the auto-window is materialised
      // into an explicit row mid-interaction. Without this, the click
      // event fired after pointerdown either binds to the wrong source
      // (windows shifting by index) or never fires at all (rect unmounts).
      const autoId = `auto-W-${seg.room_id}-${seg.side}-${mc.toFixed(2)}`;
      out.push({
        id: autoId,
        type: "fixed",
        axis: seg.axis,
        coord: seg.coord,
        centre: mc,
        len: winLen,
        span_lo: seg.span_lo,
        span_hi: seg.span_hi,
        _autoRoom: seg.room_id,
        _autoSide: seg.side,
        _autoPosition: position,
      });
    });
  });
  // V2.0.0 — defensive dedupe (see findInteriorDoorPairs).
  const seenIds = new Map();
  out.forEach((w) => {
    const n = (seenIds.get(w.id) || 0) + 1;
    seenIds.set(w.id, n);
    if (n > 1) {
      w.id = `${w.id}-dup${n}`;
    }
  });
  return out;
}

export { DOOR_RADIUS_M };
